import { useEffect, useMemo, useState } from "react";
import { inputToMinor, isPositiveMinor, minorToInput } from "../utils/money";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { getCustomerAddresses } from "pages/GenericOrderDetailsPage/apis";
import { getRelationShipManager } from "service/moneeflo/legacy-lookup-api";
import {
  createForexDelivery,
  updateForexDelivery,
  type DeliveryDestination,
  type DeliveryMode,
  type DeliveryStatusValue,
} from "service/moneeflo/legacy-forex-desk-api";
import type { CoreDelivery } from "service/moneeflo/legacy-forex-order-api";
import DeskDialog, { Field, inputClass } from "./DeskDialog";
import { ddmmyyyyToIso, isoToDdmmyyyy } from "./DateTextInput";

export type DeliveryLine = {
  id: string;
  label: string;
  currency: string;
  /** Foreign amount on the order. */
  quantity: number;
  /** Foreign amount, in paise, not yet on a delivery that is still going ahead. */
  remainingMinor: string;
};

const MODES: { value: DeliveryMode; label: string }[] = [
  { value: "self", label: "Delivery" },
  { value: "courier", label: "Courier" },
  { value: "pickup", label: "Pickup" },
];

const blankAddress = { line1: "", line2: "", landmark: "", city: "", state: "", postalCode: "", country: "" };


/**
 * Adding a delivery to a forex order, or changing one that exists. The lines
 * offered are the ones with foreign currency not yet on a live delivery, and
 * each is sent with how much of it goes. A pickup has no address; the other
 * modes take one of the customer's saved addresses or a new one. Core says
 * whether the delivery may be made, and its sentence is shown when it won't.
 */
export default function ForexDeliveryDialog({
  open,
  onOpenChange,
  customerId,
  lines,
  delivery,
  defaultAddressId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string | null;
  lines: DeliveryLine[];
  /** Present when changing a delivery; absent when adding one. */
  delivery?: CoreDelivery | null;
  /** The order's own delivery address, preselected when adding. */
  defaultAddressId?: string | null;
}) {
  const editing = !!delivery;
  const [mode, setMode] = useState<DeliveryMode>("self");
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [addressChoice, setAddressChoice] = useState<string>("new");
  const [address, setAddress] = useState(blankAddress);
  const [channel, setChannel] = useState("");
  const [scheduledOn, setScheduledOn] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: saved } = useQuery({
    queryKey: ["customerAddresses", customerId],
    queryFn: () => getCustomerAddresses(customerId as string),
    enabled: open && !!customerId,
    staleTime: 60 * 1000,
  });
  const { data: staff } = useQuery({
    queryKey: ["assignableStaff"],
    queryFn: async () => (await getRelationShipManager({} as never)).data,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const remainingKey = lines.map((l) => `${l.id}:${l.remainingMinor}`).join(",");
  // Start from the delivery being changed, or from a clean form.
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (delivery) {
      setMode(delivery.mode);
      setChannel(delivery.channel ?? "");
      setScheduledOn(isoToDdmmyyyy(delivery.scheduledOn?.slice(0, 10)));
      setAssignedTo(delivery.assignedTo?.id ?? "");
      setNotes(delivery.notes ?? "");
      setAddressChoice("new");
      setAddress({
        line1: delivery.address?.line1 ?? "",
        line2: delivery.address?.line2 ?? "",
        landmark: delivery.address?.landmark ?? "",
        city: delivery.address?.city ?? "",
        state: delivery.address?.state ?? "",
        postalCode: delivery.address?.postalCode ?? "",
        country: delivery.address?.country ?? "",
      });
    } else {
      setMode("self");
      setChannel("");
      setScheduledOn("");
      setAssignedTo("");
      setNotes("");
      setAddress(blankAddress);
      setPicked(Object.fromEntries(lines.filter((l) => isPositiveMinor(l.remainingMinor)).map((l) => [l.id, minorToInput(l.remainingMinor)])));
    }
    // The order is refreshed after each delivery: what is left is offered again when the figures change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, delivery, remainingKey]);

  // A new delivery starts on the order's own address, else the first saved one.
  useEffect(() => {
    if (!open || editing || !saved) return;
    const wanted = saved.find((a) => a.id === defaultAddressId) ?? saved.find((a) => a.isDefault) ?? saved[0];
    setAddressChoice(wanted ? wanted.id : "new");
  }, [open, editing, saved, defaultAddressId]);

  const mutation = useMutation({
    mutationFn: async () => {
      const destination: DeliveryDestination | undefined =
        mode === "pickup"
          ? undefined
          : addressChoice !== "new"
            ? { addressId: addressChoice }
            : {
                line1: address.line1,
                line2: address.line2 || undefined,
                landmark: address.landmark || undefined,
                city: address.city,
                state: address.state || undefined,
                postalCode: address.postalCode,
                country: address.country || undefined,
              };
      if (delivery) {
        return updateForexDelivery(delivery.id, {
          channel,
          destination,
          // A blank clears the date, the person or the note: core is told so, not left as it was.
          scheduledOn: scheduledIso ?? "",
          assignedTo,
          notes,
        });
      }
      const items = lines
        .filter((l) => picked[l.id] !== undefined)
        .map((l) => ({ lineId: l.id, fxAmount: Number(picked[l.id]) }));
      return createForexDelivery({ mode, channel, destination, items, scheduledOn: scheduledIso ?? "", assignedTo, notes });
    },
    onSuccess: () => {
      toast.success(editing ? "Delivery updated" : "Delivery added");
      queryClient.invalidateQueries(["orderDetails"]);
      onOpenChange(false);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "The delivery could not be saved."),
  });

  const offered = useMemo(() => lines.filter((l) => isPositiveMinor(l.remainingMinor)), [lines]);
  const itemsOk =
    editing ||
    (Object.keys(picked).length > 0 &&
      offered.every((l) => {
        if (picked[l.id] === undefined) return true;
        const minor = inputToMinor(picked[l.id]);
        return minor !== null && minor > 0n && minor <= BigInt(l.remainingMinor);
      }));
  const scheduledIso = ddmmyyyyToIso(scheduledOn);
  const dateOk = scheduledIso !== null;
  const addressOk =
    mode === "pickup" ||
    addressChoice !== "new" ||
    (address.line1.trim().length >= 2 && address.city.trim().length >= 2 && address.postalCode.trim().length >= 4);

  const set = (key: keyof typeof blankAddress) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setAddress((prev) => ({ ...prev, [key]: e.target.value }));

  return (
    <DeskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? "Edit delivery" : "Add delivery"}
      busy={mutation.isLoading}
      submitDisabled={!itemsOk || !addressOk || !dateOk}
      error={error}
      onSubmit={() => {
        setError(null);
        mutation.mutate();
      }}
    >
      <Field label="Delivery mode" htmlFor="delivery-mode">
        <select
          id="delivery-mode"
          className={inputClass}
          value={mode}
          disabled={editing}
          onChange={(e) => setMode(e.target.value as DeliveryMode)}
        >
          {MODES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      </Field>

      {!editing && (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium text-[#333333]">Currency on this delivery</legend>
          {offered.length === 0 ? (
            <p className="text-sm text-[#666666]">Every line is already on a delivery.</p>
          ) : (
            offered.map((line) => (
              <div key={line.id} className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  aria-label={`Include ${line.label}`}
                  checked={picked[line.id] !== undefined}
                  onChange={(e) =>
                    setPicked((prev) => {
                      const next = { ...prev };
                      if (e.target.checked) next[line.id] = minorToInput(line.remainingMinor);
                      else delete next[line.id];
                      return next;
                    })
                  }
                />
                <span className="flex-1 text-[#333333]">{line.label}</span>
                <input
                  aria-label={`${line.currency} amount for ${line.label}`}
                  className={`${inputClass} w-28`}
                  type="number"
                  min={0}
                  max={minorToInput(line.remainingMinor)}
                  step="0.01"
                  disabled={picked[line.id] === undefined}
                  value={picked[line.id] ?? ""}
                  onChange={(e) => setPicked((prev) => ({ ...prev, [line.id]: e.target.value }))}
                />
              </div>
            ))
          )}
        </fieldset>
      )}

      {mode !== "pickup" && (
        <>
          {saved && saved.length > 0 && (
            <Field label="Address" htmlFor="delivery-address">
              <select
                id="delivery-address"
                className={inputClass}
                value={addressChoice}
                onChange={(e) => setAddressChoice(e.target.value)}
              >
                {saved.map((a) => (
                  <option key={a.id} value={a.id}>
                    {[a.label, a.line1, a.city, a.postalCode].filter(Boolean).join(", ")}
                  </option>
                ))}
                <option value="new">{editing ? "Use this address" : "A new address"}</option>
              </select>
            </Field>
          )}
          {addressChoice === "new" && (
            <div className="flex flex-col gap-3">
              <Field label="Address line 1" htmlFor="addr-line1">
                <input id="addr-line1" className={inputClass} value={address.line1} onChange={set("line1")} />
              </Field>
              <Field label="Address line 2" htmlFor="addr-line2">
                <input id="addr-line2" className={inputClass} value={address.line2} onChange={set("line2")} />
              </Field>
              <Field label="Landmark" htmlFor="addr-landmark">
                <input id="addr-landmark" className={inputClass} value={address.landmark} onChange={set("landmark")} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="City" htmlFor="addr-city">
                  <input id="addr-city" className={inputClass} value={address.city} onChange={set("city")} />
                </Field>
                <Field label="State" htmlFor="addr-state">
                  <input id="addr-state" className={inputClass} value={address.state} onChange={set("state")} />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Pincode" htmlFor="addr-pin">
                  <input id="addr-pin" className={inputClass} inputMode="numeric" value={address.postalCode} onChange={set("postalCode")} />
                </Field>
                <Field label="Country" htmlFor="addr-country">
                  <input id="addr-country" className={inputClass} value={address.country} onChange={set("country")} />
                </Field>
              </div>
            </div>
          )}
        </>
      )}

      {mode === "courier" && (
        <Field label="Courier" htmlFor="delivery-channel">
          <input id="delivery-channel" className={inputClass} value={channel} onChange={(e) => setChannel(e.target.value)} />
        </Field>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Scheduled for" htmlFor="delivery-date">
          <input
            id="delivery-date"
            className={inputClass}
            inputMode="numeric"
            placeholder="dd/mm/yyyy"
            value={scheduledOn}
            aria-invalid={!dateOk}
            onChange={(e) => setScheduledOn(e.target.value)}
          />
          {!dateOk && <span role="alert" className="text-xs text-[#B00020]">Use dd/mm/yyyy, for example 25/10/2026.</span>}
        </Field>
        <Field label="Assign to" htmlFor="delivery-assignee">
          <select id="delivery-assignee" className={inputClass} value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
            <option value="">Nobody yet</option>
            {(staff ?? []).map((person) => (
              <option key={String(person.id)} value={String(person.id)}>
                {person.fname}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Notes" htmlFor="delivery-notes">
        <textarea id="delivery-notes" className={`${inputClass} min-h-[64px]`} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </DeskDialog>
  );
}

/** The next statuses core allows from each, in the words the desk uses. */
export const NEXT_STATUSES: Record<string, { value: DeliveryStatusValue; label: string }[]> = {
  pending: [
    { value: "ready_to_deliver", label: "Ready to deliver" },
    { value: "cancelled", label: "Cancelled" },
  ],
  ready_to_deliver: [
    { value: "delivered", label: "Delivered" },
    { value: "failed", label: "Failed" },
    { value: "pending", label: "Back to pending" },
    { value: "cancelled", label: "Cancelled" },
  ],
};

/**
 * Moving one delivery on. Ready and Delivered run core's release rules, so a
 * sale that is not paid in full, or an encashment whose customs declaration
 * has not been seen, comes back refused with the reason, and the delivery stays
 * as it was. A failed delivery needs the reason it failed.
 */
export function DeliveryStatusDialog({
  open,
  onOpenChange,
  delivery,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  delivery: CoreDelivery | null;
}) {
  const options = delivery ? NEXT_STATUSES[delivery.status] ?? [] : [];
  const [status, setStatus] = useState<DeliveryStatusValue | "">("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setStatus(options[0]?.value ?? "");
      setReason("");
      setError(null);
    }
  }, [open, delivery?.id]);

  const mutation = useMutation({
    mutationFn: () =>
      updateForexDelivery(delivery!.id, { status: status as DeliveryStatusValue, reason: status === "failed" ? reason : "" }),
    onSuccess: () => {
      toast.success("Delivery status updated");
      queryClient.invalidateQueries(["orderDetails"]);
      onOpenChange(false);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "The delivery could not be updated."),
  });

  return (
    <DeskDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Update delivery status"
      busy={mutation.isLoading}
      submitDisabled={!status || (status === "failed" && reason.trim().length < 3)}
      error={error}
      onSubmit={() => {
        setError(null);
        mutation.mutate();
      }}
    >
      <Field label="New status" htmlFor="delivery-status">
        <select
          id="delivery-status"
          className={inputClass}
          value={status}
          onChange={(e) => setStatus(e.target.value as DeliveryStatusValue)}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </Field>
      {status === "failed" && (
        <Field label="Why it failed" htmlFor="delivery-reason">
          <textarea id="delivery-reason" className={`${inputClass} min-h-[64px]`} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      )}
    </DeskDialog>
  );
}
